import { getEntity } from '@/backend/entity'
import { getProject } from '@/backend/getProject'
import { useCaseQuery } from '@/data/Cases/useCaseQuery'
import type { CaseOutput } from '@/modules/Cases/types'
import { AGENT_NAME_FALLBACK } from '@/modules/Project/constants'
import { serializeEntity } from '@/modules/Project/serializer'
import type { Entity } from '@/modules/Project/types'
import { serializeProject, type Project } from '@/modules/Projects/useProjects'
import { useQueries, type UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed } from 'vue'

/** Loads and returns all outputs for a given case */
export const useCaseOutputQueries = (
  caseId: Ref<string | undefined | null>,
  workspaceId: Ref<string>,
  options: Partial<UseQueryOptions<CaseOutputWithAssociatedData>> = {},
) => {
  const caseQuery = useCaseQuery(caseId, workspaceId)

  return useQueries({
    queries: computed(
      () =>
        caseQuery.data.value?.outputs.map<UseQueryOptions<CaseOutputWithAssociatedData>>(
          (output) => ({
            queryKey: ['case-output', caseId.value, output.id, output.entityId, output.projectId],
            queryFn: async () => {
              const [project, entity] = await Promise.all([
                getProject(workspaceId.value, output.projectId),
                getEntity(workspaceId.value, output.projectId, output.entityId),
              ])

              if (!project.ok || !entity.ok) {
                throw new Error('Failed to fetch project or entity')
              }

              return {
                ...output,
                project: {
                  ...serializeProject(project.data),
                  name: project.data.name || AGENT_NAME_FALLBACK,
                },
                entity: serializeEntity(entity.data, project.data.workspace_id),
              }
            },
            enabled: () => !!workspaceId.value,
            ...options,
          }),
        ) || [],
    ),
  })
}

export type CaseOutputWithAssociatedData = CaseOutput & {
  project: Project & { name: string }
  entity: Entity
}
